import { ListView } from '@ds/components'
import { type ChangeEvent, useState, useMemo, useEffect } from 'react'
import { useList } from '@refinedev/core'
import type { TPublisherRequest } from '@modules/types'
import { PUBLISHER_EXPAND_PRESETS } from '@modules/api'
import { useEnumContext } from '@modules/enums/context/enum'
import { type TFormattedRequestData, formatRequestData } from '@utils/requestFormatter'
import { RequestsGrid } from '../RequestShow'

export const ShareList = () => {
  useEffect(() => {
    document.title = 'Publisher Panel'
    return () => {
      document.title = 'Publisher Panel'
    }
  }, [])

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 12,
  })
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set())

  const { enums, loading: enumsLoading } = useEnumContext()

  const { data, isLoading, isError } = useList<TPublisherRequest>({
    resource: 'publisher-request',
    pagination: {
      mode: 'server',
      current: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    },
    dataProviderName: 'share',
    meta: PUBLISHER_EXPAND_PRESETS.CLIENT_REQUEST,
    queryOptions: {
      enabled: true,
    },
  })

  const requests = useMemo(() => data?.data || [], [data?.data])
  const total = data?.total || 0

  const formattedRequestsData = useMemo(() => {
    const formatted: Record<number, TFormattedRequestData> = {}

    requests.forEach(request => {
      formatted[request.id] = formatRequestData(request, enums, enumsLoading)
    })

    return formatted
  }, [requests, enums, enumsLoading])

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    setPagination(prev => ({ ...prev, pageIndex: page - 1 }))
  }

  const handleToggleCard = (requestId: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev)
      if (newSet.has(requestId)) {
        newSet.delete(requestId)
      } else {
        newSet.add(requestId)
      }
      return newSet
    })
  }

  const totalPages = Math.ceil(total / pagination.pageSize)

  return (
    <ListView overflow>
      <RequestsGrid
        requests={requests}
        formattedRequestsData={formattedRequestsData}
        isLoading={isLoading}
        isError={isError}
        totalPages={totalPages}
        currentPage={pagination.pageIndex + 1}
        onPageChange={handlePageChange}
        expandedCards={expandedCards}
        onToggleCard={handleToggleCard}
      />
    </ListView>
  )
}
